import { makeStyles } from "@material-ui/core";
import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useHistory, useParams } from "react-router-dom";
import { Select } from "antd";
import { REPORT_FONT, useReportFonts } from "../reportFonts";
import {
  FILTERS_CONFIG,
  initFilterValues,
  isFilterActive,
  getDisplayValue,
  getDefaultValue,
  getActiveFilterCount,
  areVisibleFilterValuesEqual,
  HorizontalDateRangeFilter,
  FilterApplyButton,
} from "./GlobalFilterPanel";
import { useReportFilterOptions } from "./useReportFilterOptions";
import {
  pruneInvalidCascadeSelections,
  getFilterFieldOptions,
  toFilterSelectOptions,
  FILTER_SELECT_SEARCH_PROPS,
  GlobalMultiSelectFilter,
  expandAllFilterSelections,
  isAllFilterSelected,
} from "./filterRelationshipUtils";
import {
  buildIndirectCascadedOptions,
  buildFringeCascadedOptions,
  pruneIndirectSelections,
  pruneFringeSelections,
  normalizeIndirectRelRows,
  normalizeFringeRelRows,
} from "./indirectFilterRelationshipUtils";
import {
  getIndirectFilterRelationshipsAPI,
  getIndirectFringeFilterRelationshipsAPI,
  getIndirectLaborMonthsAPI,
  getIndirectVersionsAPI,
} from "../../../api/reports-api";
import {
  usePlByProgramStyles,
  PlPageHeader,
  PL_PAGE_BG,
} from "./pl-by-program/plByProgramTheme";
import BackButton from "../BackButton";
import { goToReportsCatalog } from "../reportNav";

import IndirectPL from "./indirect-report/IndirectPL";
import SummaryVsBudgetDept from "./indirect-report/SummaryVsBudgetDept";
import SummaryVsPriorDept from "./indirect-report/SummaryVsPriorDept";
import EmployeeLabor from "./indirect-report/EmployeeLabor";
import EmployeeLaborComparison from "./indirect-report/EmployeeLaborComparison";
import FringePL from "./indirect-report/FringePL";
import SummaryVsBudgetFringe from "./indirect-report/SummaryVsBudgetFringe";
import SummaryVsPriorFringe from "./indirect-report/SummaryVsPriorFringe";
import ForecastVersionComparisonFringe from "./indirect-report/ForecastVersionComparisonFringe";
import Loading from "../../../common-components/Loading";

import { TH_BG_BAND, ACCENT_COLOR } from "../reportTableConfig";

const ACCENT = ACCENT_COLOR;
const BORDER = "#E5E7EB";

const TABS = [
  { key: "indirect_pl", label: "Indirect P&L" },
  { key: "summary_vs_budget_dept", label: "Summary Vs Budget by Dept" },
  { key: "summary_vs_prior_dept", label: "Summary Vs Prior Fcst by Dept" },
  { key: "employee_labor", label: "Employee Labor" },
  { key: "employee_labor_people", label: "Employee Labor (People) Comparison" },
  { key: "fringe_pl", label: "Fringe P&L" },
  { key: "summary_vs_budget_fringe", label: "Summary Vs Budget — Fringe" },
  { key: "summary_vs_prior_fringe", label: "Summary Vs Prior Fcst — Fringe" },
  { key: "forecast_version_comparison_fringe", label: "Forecast Version Comparison — Fringe" },
];

/* ── Month → Quarter helpers ─────────────────────────────────── */
const MONTH_TO_QTR_NUM = {
  jan: 1,
  january: 1,
  feb: 1,
  february: 1,
  mar: 1,
  march: 1,
  apr: 2,
  april: 2,
  may: 2,
  jun: 2,
  june: 2,
  jul: 3,
  july: 3,
  aug: 3,
  august: 3,
  sep: 3,
  september: 3,
  oct: 4,
  october: 4,
  nov: 4,
  november: 4,
  dec: 4,
  december: 4,
};
function qtrNum(q) {
  return parseInt(String(q).replace(/^Q/i, ""), 10);
}
function monthQtrNum(m) {
  const k = String(m).toLowerCase();
  return MONTH_TO_QTR_NUM[k] ?? MONTH_TO_QTR_NUM[k.slice(0, 3)];
}

/* ── Per-tab visible filter keys ─────────────────────────────── */
const TAB_FILTERS = {
  indirect_pl: [
    "year",
    "quarter",
    "monthName",
    "department",
    "subDepartment",
    "employeeName",
    "costTypeIndirect",
  ],
  summary_vs_budget_dept: [
    "year",
    "quarter",
    "monthName",
    "department",
    "subDepartment",
    "employeeName",
    "costTypeIndirect",
  ],
  summary_vs_prior_dept: [
    "year",
    "quarter",
    "monthName",
    "department",
    "subDepartment",
    "employeeName",
    "costTypeIndirect",
  ],
  employee_labor: [
    "year",
    "quarter",
    "monthName",
    "department",
    "subDepartment",
    "employeeName",
  ],
  employee_labor_people: [
    "year",
    "quarter",
    "monthName",
    "department",
    "subDepartment",
    "employeeName",
  ],
  fringe_pl: ["year", "quarter", "monthName", "costTypeFringe", "account"],
  summary_vs_budget_fringe: ["year", "quarter", "monthName", "costTypeFringe", "account"],
  summary_vs_prior_fringe: ["year", "quarter", "monthName", "costTypeFringe", "account"],
  forecast_version_comparison_fringe: ["year", "quarter", "monthName", "costTypeFringe", "account"],
};

const useStyles = makeStyles(() => ({
  page: {
    backgroundColor: PL_PAGE_BG,
    fontFamily: REPORT_FONT,
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh - 80px)",
  },

  topBar: {
    position: "sticky",
    top: 0,
    zIndex: 20,
    background: "#fff",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexShrink: 0,
  },
  crumb: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#9CA3AF",
    minWidth: 0,
  },
  crumbLink: {
    cursor: "pointer",
    color: "#9CA3AF",
    "&:hover": { color: "#6B7280" },
  },
  crumbSep: { color: "#D1D5DB", userSelect: "none" },
  crumbCur: { color: "#111827", fontWeight: 500 },

  topActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  actionBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 32,
    padding: "0 12px",
    borderRadius: 6,
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 0.15s, border-color 0.15s",
    "&:hover": { background: "#F9FAFB", borderColor: "#D1D5DB" },
  },
  actionBtnActive: {
    borderColor: ACCENT,
    background: ACCENT,
    color: "#fff",
    "&:hover": { background: "#4338CA", borderColor: "#4338CA" },
  },
  actionBtnIconOnly: {
    width: 32,
    minWidth: 32,
    padding: 0,
    justifyContent: "center",
  },
  "@keyframes dlspin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  dlSpin: { animation: "$dlspin 0.7s linear infinite" },
  filterBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 18,
    height: 18,
    padding: "0 5px",
    borderRadius: 9,
    background: "rgba(255,255,255,0.25)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
  },

  tabBar: {
    background: "#fff",
    borderBottom: `1px solid ${BORDER}`,
    display: "flex",
    flexWrap: "wrap",
    flexShrink: 0,
    padding: "0 24px",
    gap: "4px 0",
  },
  tab: {
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
    whiteSpace: "nowrap",
    border: "none",
    borderBottom: "2px solid transparent",
    background: "none",
    marginBottom: -1,
    transition: "color 0.15s, border-color 0.15s",
    "&:hover": { color: ACCENT },
  },
  tabActive: {
    color: ACCENT,
    fontWeight: 700,
    borderBottom: `2px solid ${ACCENT}`,
  },

  /* ── horizontal filter bar ── */
  hFilterBar: {
    position: "relative",
    background: "#d6dbf8",
    borderTop: "2px solid #191b8c",
    borderBottom: `1px solid ${BORDER}`,
    padding: "12px 52px 12px 24px",
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: 10,
    flexShrink: 0,
  },
  hFilterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    flexShrink: 0,
    background: "#fff",
    borderRadius: 8,
    padding: "6px 10px 8px",
    minWidth: 120,
  },
  hFilterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#191b8c",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  },
  hFilterInput: {
    height: 26,
    padding: "0 6px",
    border: "none",
    borderRadius: 4,
    fontSize: 12,
    fontFamily: REPORT_FONT,
    color: "#111827",
    outline: "none",
    minWidth: 110,
    background: "transparent",
    "&:focus": { outline: `1px solid ${ACCENT}`, borderRadius: 4 },
  },
  hFilterActions: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  hFilterApply: {
    height: 34,
    padding: "0 16px",
    borderRadius: 6,
    border: "none",
    background: ACCENT,
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: REPORT_FONT,
    whiteSpace: "nowrap",
    "&:hover": { background: "#4338CA" },
  "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  "&:disabled:hover": { background: "inherit" },
  },
  hFilterClearAll: {
    alignSelf: "flex-end",
    height: 34,
    padding: "0 12px",
    borderRadius: 6,
    border: `1px solid ${BORDER}`,
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    flexShrink: 0,
    whiteSpace: "nowrap",
    fontFamily: REPORT_FONT,
    "&:hover": { color: ACCENT, borderColor: ACCENT },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  hFilterClose: {
    position: "absolute",
    top: 10,
    right: 10,
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "2px solid #191b8c",
    background: TH_BG_BAND,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: REPORT_FONT,
    "&:hover": { background: "#111827" },
  },

  /* ── active filter chips ── */
  chipsBar: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    background: "#F8F9FF",
    borderBottom: `1px solid ${BORDER}`,
    flexWrap: "wrap",
    flexShrink: 0,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    height: 26,
    padding: "0 10px",
    borderRadius: 20,
    background: TH_BG_BAND,
    border: `1px solid ${TH_BG_BAND}`,
    fontSize: 11,
    fontWeight: 500,
    color: "#F1F5F9",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  chipLabel: { color: "#94A3B8", fontWeight: 400, marginRight: 2 },
  chipClear: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#94A3B8",
    fontSize: 10,
    padding: 0,
    marginLeft: 2,
    "&:hover": { background: "rgba(255,255,255,0.15)", color: "#fff" },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  chipsClearAll: {
    fontSize: 11,
    fontWeight: 600,
    color: "#6B7280",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "0 6px",
    marginLeft: 4,
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": { color: ACCENT },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },

  body: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  content: {
    flex: 1,
    padding: "0 16px",
    overflowY: "auto",
    minWidth: 0,
  },

  headerContainer: { margin: "20px 0 0", padding: "0 16px" },
}));

function FilterIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function isOnlyDefaultYear(filterValues, vKeys) {
  const keys = vKeys
    ? vKeys.filter((k) => k !== "year")
    : FILTERS_CONFIG.filter((f) => f.key !== "year").map((f) => f.key);
  return (
    !keys.some((k) => {
      const f = FILTERS_CONFIG.find((fc) => fc.key === k);
      return f && isFilterActive(f, filterValues[k]);
    }) && String(filterValues.year) === "2026"
  );
}

/* ── Horizontal filter bar component ─────────────────────────── */
function HorizontalFilterBar({
  visibleKeys,
  draft,
  dropdownOptions,
  onChange,
  onClearAll,
  onApply,
  onClose,
  classes,
  clearAllDisabled,
  applyDisabled,
}) {
  const filters = visibleKeys
    ? FILTERS_CONFIG.filter((f) => visibleKeys.includes(f.key))
    : FILTERS_CONFIG;

  const selectStyle = { minWidth: 110, maxWidth: 180, fontSize: 12 };

  return (
    <div className={classes.hFilterBar}>
      {filters.map((f) => {
        const opts = toFilterSelectOptions(
          f.key,
          getFilterFieldOptions(f, dropdownOptions),
        );
        const val = draft[f.key];

        return (
          <div key={f.key} className={classes.hFilterGroup}>
            <span className={classes.hFilterLabel}>{f.label}</span>

            {f.type === "select" && (
              <Select
                size="small"
                style={selectStyle}
                value={val || (f.key === "year" ? opts[0]?.value : "All")}
                onChange={(v) =>
                  onChange(f.key, v === "All" ? f.defaultValue : v)
                }
                options={
                  f.key === "year"
                    ? opts
                    : [{ value: "All", label: "All" }, ...opts]
                }
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}

            {f.type === "multiselect" && (
              <GlobalMultiSelectFilter
                size="small"
                style={selectStyle}
                value={val}
                options={opts}
                onChange={(v) => onChange(f.key, v)}
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}

            {f.type === "text" && (
              <input
                className={classes.hFilterInput}
                value={val || ""}
                placeholder="All"
                onChange={(e) => onChange(f.key, e.target.value)}
              />
            )}
            {f.type === "daterange" && (
              <HorizontalDateRangeFilter
                value={val}
                onChange={(next) => onChange(f.key, next)}
              />
            )}
          </div>
        );
      })}

      <div className={classes.hFilterActions}>
        <button
          className={classes.hFilterClearAll}
          onClick={onClearAll}
          disabled={clearAllDisabled}
        >
          Clear All
        </button>
        <FilterApplyButton
          draft={draft}
          onApply={onApply}
          className={classes.hFilterApply}
          disabled={applyDisabled}
        >
          Apply
        </FilterApplyButton>
      </div>

      <button
        className={classes.hFilterClose}
        onClick={onClose}
        title="Close Filters"
      >
        ✕
      </button>
    </div>
  );
}

export default function IndirectReport() {
  const classes = useStyles();
  const plClasses = usePlByProgramStyles();
  useReportFonts();
  const history = useHistory();

  const { tab: tabParam } = useParams();
  const activeTab = TABS.find((t) => t.key === tabParam)?.key ?? TABS[0].key;
  const [filterOpen, setFilterOpen] = useState(true);
  /* tabActualsMonth: the MTD month driving each Summary Vs Budget/Prior tab's own
     requests + dropdown — kept independent per tab so picking a month on one
     tab never affects another. Must never go blank for a visited tab, or that
     tab's fetches break.
     headerMonth: purely for the page header's date text — reported by every
     tab after each load, and allowed to go blank (null) when that tab's own
     API response has no actualsMonth data for the current selection/version. */
  const [tabActualsMonth, setTabActualsMonth] = useState({});
  const [headerMonth, setHeaderMonth] = useState(null);
  const setActualsMonthForTab = useCallback(
    (tab) => (updater) =>
      setTabActualsMonth((prev) => ({
        ...prev,
        [tab]:
          typeof updater === "function" ? updater(prev[tab] ?? null) : updater,
      })),
    [],
  );
  const [downloading, setDownloading] = useState(false);
  const tabDownloadRef = useRef(null);

  /* Per-tab filter state — each tab has its own independent applied/draft values */
  const [tabApplied, setTabApplied] = useState({});
  const [tabDraft, setTabDraft] = useState({});

  /* Stable default — created once so render-path fallback doesn't change reference each render */
  const defaultFilters = useMemo(() => initFilterValues(), []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Always reflects the active tab's filters; falls back to defaults on first visit */
  const appliedFilters = tabApplied[activeTab] ?? defaultFilters;
  const draftFilters = tabDraft[activeTab] ?? defaultFilters;

  /* Stable setters — read activeTab from a ref so downstream useCallbacks stay stable */
  const activeTabRef = useRef(activeTab);
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const setAppliedFilters = useCallback((updater) => {
    const tab = activeTabRef.current;
    setTabApplied((prev) => ({
      ...prev,
      [tab]:
        typeof updater === "function"
          ? updater(prev[tab] ?? initFilterValues())
          : updater,
    }));
  }, []);

  const setDraftFilters = useCallback((updater) => {
    const tab = activeTabRef.current;
    setTabDraft((prev) => ({
      ...prev,
      [tab]:
        typeof updater === "function"
          ? updater(prev[tab] ?? initFilterValues())
          : updater,
    }));
  }, []);

  const visibleKeys = TAB_FILTERS[activeTab] || null;
  const { dropdownOptions, relationshipRows } = useReportFilterOptions(
    draftFilters,
    visibleKeys,
  );

  /* Indirect & fringe filter-relationship feeds — fetched once, used for cascade */
  const [indirectRelRows, setIndirectRelRows] = useState([]);
  const [fringeRelRows, setFringeRelRows] = useState([]);
  const indirectRelRowsRef = useRef([]);
  const fringeRelRowsRef = useRef([]);
  useEffect(() => {
    indirectRelRowsRef.current = indirectRelRows;
  }, [indirectRelRows]);
  useEffect(() => {
    fringeRelRowsRef.current = fringeRelRows;
  }, [fringeRelRows]);

  useEffect(() => {
    Promise.all([
      getIndirectFilterRelationshipsAPI(),
      getIndirectFringeFilterRelationshipsAPI(),
    ])
      .then(([indRes, fringeRes]) => {
        setIndirectRelRows(normalizeIndirectRelRows(indRes));
        setFringeRelRows(normalizeFringeRelRows(fringeRes));
      })
      .catch(() => {});
  }, []);

  /* Seed each dropdown tab's actualsMonth/headerMonth from the live API on
     first load instead of hardcoding the current month or version. */
  useEffect(() => {
    getIndirectVersionsAPI()
      .then((res) => {
        const data = Array.isArray(res) ? res : res?.data || [];
        const version = data[0];
        if (!version) return null;
        return getIndirectLaborMonthsAPI({
          financialYear: String(new Date().getFullYear()),
          version,
        });
      })
      .then((res) => {
        if (!res) return;
        const arr = res?.data?.actualsMonth;
        if (Array.isArray(arr) && arr.length) {
          const latest = arr[arr.length - 1];
          setTabActualsMonth((prev) => ({
            summary_vs_budget_dept: prev.summary_vs_budget_dept ?? latest,
            summary_vs_prior_dept: prev.summary_vs_prior_dept ?? latest,
            summary_vs_budget_fringe: prev.summary_vs_budget_fringe ?? latest,
            summary_vs_prior_fringe: prev.summary_vs_prior_fringe ?? latest,
          }));
          setHeaderMonth((prev) => prev ?? latest);
        }
      })
      .catch(() => {});
  }, []);

  /** Only pass filters visible on the active tab — hidden values stay in state for when user returns */
  const effectiveFilters = useMemo(() => {
    const visible = new Set(TAB_FILTERS[activeTab] || []);
    const next = { ...appliedFilters };
    FILTERS_CONFIG.forEach((f) => {
      if (!visible.has(f.key)) next[f.key] = getDefaultValue(f);
    });
    let expandOpts = dropdownOptions;
    if (indirectRelRows.length) {
      expandOpts = {
        ...expandOpts,
        ...buildIndirectCascadedOptions(indirectRelRows, appliedFilters),
      };
    }
    if (fringeRelRows.length) {
      expandOpts = {
        ...expandOpts,
        ...buildFringeCascadedOptions(fringeRelRows, appliedFilters),
      };
    }
    return expandAllFilterSelections(next, expandOpts);
  }, [appliedFilters, activeTab, dropdownOptions, indirectRelRows, fringeRelRows]);

  /* Sync draft when filter bar opens */
  useEffect(() => {
    if (filterOpen) {
      let draft = pruneInvalidCascadeSelections(
        appliedFilters,
        relationshipRows,
        visibleKeys,
      );
      draft = pruneIndirectSelections(draft, indirectRelRows);
      draft = pruneFringeSelections(draft, fringeRelRows);
      setDraftFilters(draft);
    }
  }, [
    filterOpen,
    appliedFilters,
    relationshipRows,
    indirectRelRows,
    fringeRelRows,
    visibleKeys,
  ]);

  const filterCount = getActiveFilterCount(appliedFilters, visibleKeys);

  const activeChips = (
    visibleKeys
      ? FILTERS_CONFIG.filter((f) => visibleKeys.includes(f.key))
      : FILTERS_CONFIG
  ).filter((f) => isFilterActive(f, appliedFilters[f.key]));

  const clearChip = useCallback((f) => {
    setAppliedFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
    setDraftFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
  }, []);

  const clearAllApplied = useCallback(() => {
    setAppliedFilters((prev) => {
      const next = { ...prev };
      (visibleKeys || FILTERS_CONFIG.map((f) => f.key)).forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    });
    setDraftFilters((prev) => {
      const next = { ...prev };
      (visibleKeys || FILTERS_CONFIG.map((f) => f.key)).forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    });
  }, [visibleKeys]);

  const handleDraftChange = useCallback(
    (key, value) => {
      setDraftFilters((prev) => {
        let next = { ...prev, [key]: value };
        if (key === "monthName" && Array.isArray(value) && !isAllFilterSelected(value)) {
          const qNums = new Set(value.map(monthQtrNum).filter(Boolean));
          const quarterOpts = dropdownOptions.quarter || [];
          next.quarter = quarterOpts.filter((q) => qNums.has(qtrNum(q)));
        }
        next = pruneIndirectSelections(next, indirectRelRowsRef.current);
        next = pruneFringeSelections(next, fringeRelRowsRef.current);
        return next;
      });
    },
    [dropdownOptions],
  );

  const handleApply = useCallback(() => {
    let applied = pruneInvalidCascadeSelections(
      draftFilters,
      relationshipRows,
      visibleKeys,
    );
    applied = pruneIndirectSelections(applied, indirectRelRows);
    applied = pruneFringeSelections(applied, fringeRelRows);
    setAppliedFilters(applied);
    setFilterOpen(false);
  }, [
    draftFilters,
    relationshipRows,
    indirectRelRows,
    fringeRelRows,
    visibleKeys,
  ]);

  const clearAll = useCallback(() => {
    setDraftFilters((prev) => {
      const next = { ...prev };
      (visibleKeys || FILTERS_CONFIG.map((f) => f.key)).forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    });
  }, [visibleKeys]);

  /* Derive dropdown options: month→quarter + indirect/fringe cascade */
  const derivedDropdownOptions = useMemo(() => {
    let result = dropdownOptions;
    const selMonths = draftFilters.monthName;
    if (selMonths?.length && !isAllFilterSelected(selMonths)) {
      const qNums = new Set(selMonths.map(monthQtrNum).filter(Boolean));
      result = {
        ...result,
        quarter: (result.quarter || []).filter((q) => qNums.has(qtrNum(q))),
      };
    }
    if (indirectRelRows.length) {
      const indirectOpts = buildIndirectCascadedOptions(
        indirectRelRows,
        draftFilters,
      );
      result = { ...result, ...indirectOpts };
    }
    if (fringeRelRows.length) {
      const fringeOpts = buildFringeCascadedOptions(
        fringeRelRows,
        draftFilters,
      );
      result = { ...result, ...fringeOpts };
    }
    return result;
  }, [dropdownOptions, draftFilters, indirectRelRows, fringeRelRows]);

  const actDateLabel = headerMonth
    ? `${headerMonth} Actuals + Fcst FY-${effectiveFilters.year || new Date().getFullYear()}`
    : "";
  const TAB_HEADER = {
    indirect_pl: {
      eyebrow: "Indirect Report",
      title: "Indirect P&L",
      accent: "P&L Overview",
      badge: "Live Forecast",
      date: actDateLabel,
    },
    summary_vs_budget_dept: {
      eyebrow: "Indirect Report",
      title: "Summary Vs Budget by Dept",
      accent: "Budget Variance by Dept",
      badge: "Live Forecast",
      date: actDateLabel,
    },
    summary_vs_prior_dept: {
      eyebrow: "Indirect Report",
      title: "Summary Vs Prior Fcst by Dept",
      accent: "Prior Forecast by Dept",
      badge: "Live Forecast",
      date: actDateLabel,
    },
    employee_labor: {
      eyebrow: "Indirect Report",
      title: "Employee Labor",
      accent: "Labor Detail",
      badge: "Live Forecast",
      date: actDateLabel,
    },
    employee_labor_people: {
      eyebrow: "Indirect Report",
      title: "Employee Labor (People) Comparison",
      accent: "People Comparison",
      badge: "Live Forecast",
      date: actDateLabel,
    },
    fringe_pl: {
      eyebrow: "Indirect Report",
      title: "Fringe P&L",
      accent: "Fringe Overview",
      badge: "Live Forecast",
      date: actDateLabel,
    },
    summary_vs_budget_fringe: {
      eyebrow: "Indirect Report",
      title: "Summary Vs Budget — Fringe",
      accent: "Fringe Budget Variance",
      badge: "Live Forecast",
      date: actDateLabel,
    },
    summary_vs_prior_fringe: {
      eyebrow: "Indirect Report",
      title: "Summary Vs Prior Fcst — Fringe",
      accent: "Fringe Prior Forecast",
      badge: "Live Forecast",
      date: actDateLabel,
    },
    forecast_version_comparison_fringe: {
      eyebrow: "Indirect Report",
      title: "Forecast Version Comparison — Fringe",
      accent: "Total Fringe by EAC",
      badge: "Live Forecast",
      date: actDateLabel,
    },
  };

  const renderContent = () => {
    const f = effectiveFilters;
    const dlProps = {
      downloadRef: tabDownloadRef,
      setParentDownloading: setDownloading,
    };
    switch (activeTab) {
      case "indirect_pl":
        return (
          <IndirectPL
            filters={f}
            onHeaderMonthChange={setHeaderMonth}
            {...dlProps}
          />
        );
      case "summary_vs_budget_dept":
        return (
          <SummaryVsBudgetDept
            filters={f}
            actualsMonth={tabActualsMonth.summary_vs_budget_dept ?? null}
            onActualsMonthChange={setActualsMonthForTab(
              "summary_vs_budget_dept",
            )}
            onHeaderMonthChange={setHeaderMonth}
            {...dlProps}
          />
        );
      case "summary_vs_prior_dept":
        return (
          <SummaryVsPriorDept
            filters={f}
            actualsMonth={tabActualsMonth.summary_vs_prior_dept ?? null}
            onActualsMonthChange={setActualsMonthForTab(
              "summary_vs_prior_dept",
            )}
            onHeaderMonthChange={setHeaderMonth}
            {...dlProps}
          />
        );
      case "employee_labor":
        return (
          <EmployeeLabor
            filters={f}
            actualsMonth={headerMonth}
            onHeaderMonthChange={setHeaderMonth}
            {...dlProps}
          />
        );
      case "employee_labor_people":
        return (
          <EmployeeLaborComparison
            filters={f}
            onHeaderMonthChange={setHeaderMonth}
            {...dlProps}
          />
        );
      case "fringe_pl":
        return (
          <FringePL
            filters={f}
            onHeaderMonthChange={setHeaderMonth}
            {...dlProps}
          />
        );
      case "summary_vs_budget_fringe":
        return (
          <SummaryVsBudgetFringe
            filters={f}
            actualsMonth={tabActualsMonth.summary_vs_budget_fringe ?? null}
            onActualsMonthChange={setActualsMonthForTab(
              "summary_vs_budget_fringe",
            )}
            onHeaderMonthChange={setHeaderMonth}
            {...dlProps}
          />
        );
      case "summary_vs_prior_fringe":
        return (
          <SummaryVsPriorFringe
            filters={f}
            actualsMonth={tabActualsMonth.summary_vs_prior_fringe ?? null}
            onActualsMonthChange={setActualsMonthForTab(
              "summary_vs_prior_fringe",
            )}
            onHeaderMonthChange={setHeaderMonth}
            {...dlProps}
          />
        );
      case "forecast_version_comparison_fringe":
        return <ForecastVersionComparisonFringe filters={f} />;
      default:
        return null;
    }
  };

  const goBack = useCallback(() => goToReportsCatalog(history), [history]);

  return (
    <div className={classes.page}>
      {/* ── Top bar ── */}
      <div className={classes.topBar}>
        <BackButton onClick={goBack} ariaLabel="Back to Reports" />
        <nav className={classes.crumb}>
          <span className={classes.crumbLink} onClick={goBack}>
            Reports
          </span>
          <span className={classes.crumbSep}>&gt;</span>
          <span className={classes.crumbCur}>Indirect Report</span>
        </nav>
        <div className={classes.topActions}>
          {activeTab !== "forecast_version_comparison_fringe" && (
            <button
              type="button"
              className={`${classes.actionBtn} ${classes.actionBtnIconOnly} ${classes.actionBtnActive}`}
              title="Download"
              aria-label="Download Report"
              disabled={downloading}
              onClick={() => tabDownloadRef.current?.()}
            >
              <DownloadIcon />
            </button>
          )}
          <button
            type="button"
            className={`${classes.actionBtn} ${filterOpen || filterCount > 0 ? classes.actionBtnActive : ""}`}
            onClick={() => setFilterOpen((p) => !p)}
          >
            <FilterIcon />
            Filters
            {filterCount > 0 && (
              <span className={classes.filterBadge}>{filterCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className={classes.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`${classes.tab} ${activeTab === tab.key ? classes.tabActive : ""}`}
            onClick={() =>
              history.replace(`/projects/reports/indirect_report/${tab.key}`)
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Horizontal filter bar ── */}
      {filterOpen && (
        <HorizontalFilterBar
          visibleKeys={visibleKeys}
          draft={draftFilters}
          dropdownOptions={derivedDropdownOptions}
          onChange={handleDraftChange}
          onClearAll={clearAll}
          onApply={handleApply}
          applyDisabled={areVisibleFilterValuesEqual(draftFilters, appliedFilters, visibleKeys)}
          onClose={() => setFilterOpen(false)}
          classes={classes}
          clearAllDisabled={isOnlyDefaultYear(draftFilters, visibleKeys)}
        />
      )}

      {/* ── Active filter chips (shown when filter bar is hidden) ── */}
      {!filterOpen && activeChips.length > 0 && (
        <div className={classes.chipsBar}>
          {activeChips.map((f) => {
            const val = appliedFilters[f.key];
            const tip =
              f.type === "multiselect" && Array.isArray(val) && val.length > 1
                ? `${f.label}: ${val.join(", ")}`
                : `${f.label}: ${getDisplayValue(f, val)}`;
            return (
              <span key={f.key} className={classes.chip} title={tip}>
                <span className={classes.chipLabel}>{f.label}:</span>
                {getDisplayValue(f, val)}
                <button
                  className={classes.chipClear}
                  onClick={() => clearChip(f)}
                  title={`Clear ${f.label}`}
                  disabled={f.key === "year" && String(val) === "2026"}
                >
                  ✕
                </button>
              </span>
            );
          })}
          <button
            className={classes.chipsClearAll}
            onClick={clearAllApplied}
            disabled={isOnlyDefaultYear(appliedFilters, visibleKeys)}
          >
            Clear All
          </button>
        </div>
      )}

      {/* ── Content ── */}
      <div className={classes.body}>
        <div className={classes.content}>
          <div className={classes.headerContainer}>
            <PlPageHeader
              classes={plClasses}
              eyebrow={TAB_HEADER[activeTab].eyebrow}
              title={TAB_HEADER[activeTab].title}
              accent={TAB_HEADER[activeTab].accent}
              badge={TAB_HEADER[activeTab].badge}
              date={TAB_HEADER[activeTab].date}
            />
          </div>
          {renderContent()}
        </div>
      </div>
      {downloading && <Loading />}
    </div>
  );
}
